import amplitude, { Callback } from 'amplitude-js';
import {
  AnalyticsEventProperties,
  AnalyticsEventValue,
  ExtendedAnalyticsEventValue,
  FunnelProperties,
  isExtendedAnalyticsEvent,
  PaymentFormCompletedProperties,
  PaymentFormStateProperties,
} from 'core/analytics/entities/Analytics';
import { YEAR_IN_SECONDS } from 'core/common/constants/durations';
import {
  ATTRS_ANALYTICS_FUNNEL_LOCALE,
  ATTRS_ANALYTICS_FUNNEL_NAME,
} from 'core/common/observability/entities/SemanticAttributes';
import { toSpanAttributes } from 'core/common/observability/entities/SpanAttributes';
import { SpanName } from 'core/common/observability/entities/SpanName';
import { ObservabilitySystem } from 'core/common/observability/interfaces';
import { CookiesService } from 'core/common/services/cookie';
import { FunnelSettingsServiceInterface } from 'core/common/services/funnel-settings/interfaces';
import { decodeRedirectDomain } from 'core/common/utils/decodeRedirectDomain';
import { generateUUID } from 'core/common/utils/generateUUID';
import { isSuccessStatusCode } from 'core/common/utils/http';
import { UtmTags } from 'core/user/entities';
import { AmplitudeService } from '../../interfaces';

export class AmplitudeClientService implements AmplitudeService {
  private userId: string;
  private funnelStage = 'appnebula';
  private instance: amplitude.AmplitudeClient;
  private readonly deviceIdCookieName = 'device_id';

  constructor(
    private readonly observabilitySystem: ObservabilitySystem,
    private readonly cookiesService: CookiesService,
    private readonly funnelSettingsService: FunnelSettingsServiceInterface,
    apiKey: string,
    apiEndpoint: string,
  ) {
    this.instance = amplitude.getInstance();
    this.instance.init(apiKey, undefined, {
      apiEndpoint,
      deviceIdFromUrlParam: true,
    });
    this.userId = '';

    this.setDeviceId();
  }

  private setDeviceId(): string {
    const deviceId = this.cookiesService.get(this.deviceIdCookieName);

    if (!deviceId) {
      const newDeviceId = generateUUID();

      this.cookiesService.set(this.deviceIdCookieName, newDeviceId, {
        path: '/',
        maxAge: YEAR_IN_SECONDS,
      });
      this.instance.setDeviceId(newDeviceId);

      return newDeviceId;
    }

    this.instance.setDeviceId(deviceId);

    return deviceId;
  }

  logEvent(
    eventName: string,
    data?: Record<string, unknown>,
    callback?: Callback,
    errorCallback?: Callback,
  ) {
    const span = this.observabilitySystem.startSpan(SpanName.TRACK_ANALYTICS_AMPLITUDE, {
      attributes: {
        [ATTRS_ANALYTICS_FUNNEL_NAME]: this.funnelSettingsService.getFunnelName(),
        [ATTRS_ANALYTICS_FUNNEL_LOCALE]: this.funnelSettingsService.getFunnelLocale(),
        eventName,
        ...toSpanAttributes(data || {}),
      },
    });

    this.instance.logEvent(
      eventName,
      data,
      (code, response, details) => {
        if (isSuccessStatusCode(code)) {
          span.addEvent('Succeed', {
            code,
            response,
            reason: details && details.reason,
          });
          span.succeed();
        }

        if (callback) {
          callback(code, response, details);
        }
      },
      (code, body, details) => {
        span.addEvent('Failed', {
          code,
          body,
          reason: details && details.reason,
        });
        span.fail(body);

        if (errorCallback) {
          errorCallback(code, body, details);
        }
      },
    );
  }

  trackEvent(event: AnalyticsEventValue, properties: AnalyticsEventProperties = {}) {
    if (isExtendedAnalyticsEvent(event)) {
      this.trackExtendedEvent(event, properties);
      return;
    }

    this.logEvent(event, properties);
  }

  private trackExtendedEvent(
    event: ExtendedAnalyticsEventValue,
    properties: AnalyticsEventProperties = {},
  ) {
    this.logEvent(event.name, { ...event.properties, ...properties });
  }

  setUserProperties(userData: Record<string, unknown>) {
    this.instance.setUserProperties(userData);
  }

  setFeatureFlagsAttributes(attributes: Record<string, unknown>) {
    return this.setUserProperties({
      feature_flags_attributes: attributes,
    });
  }

  setFunnelProperties({ funnel, flow, language }: FunnelProperties) {
    this.setUserProperties({
      funnel,
      quiz: funnel,
      domain: this.funnelSettingsService.getDomainName(),
      nfb_flow: flow,
      language,
      funnel_stage: this.funnelStage,
    });
  }

  setUtmTags(tags: UtmTags) {
    this.setUserProperties({
      media_source: tags.utm_source,
      ...tags,
      ...(tags.rf ? { rf: decodeRedirectDomain(tags.rf) } : {}),
    });
  }

  getDeviceId(): string {
    return this.instance.options.deviceId || '';
  }

  getSessionId(): string {
    return String(this.instance.getSessionId());
  }

  setUserId(userId: string) {
    this.instance.setUserId(userId);
    this.userId = userId;
  }

  setUserEmail(email: string) {
    this.setUserProperties({ email });
  }

  setUserIdProperty(id: string) {
    this.setUserProperties({ appnebula_id: id });
  }

  techPageOpened(data: { page: string; context: string; userId: string }) {
    this.logEvent('tech_page_open', {
      page_name: data.page,
      funnel_stage: this.funnelStage,
    });

    this.setUserProperties({
      user_id: data.userId,
      context: data.context,
    });
  }

  trackPaymentFormState(properties: PaymentFormStateProperties) {
    this.logEvent('payment_form_state', {
      ...properties,
      funnel_stage: this.funnelStage,
    });
  }

  paymentFormCompleted(properties: PaymentFormCompletedProperties) {
    this.logEvent('payment_form_completed', {
      ...properties,
      funnel_stage: this.funnelStage,
    });
  }

  paymentRetryScreenOpened() {
    this.trackEvent('funnel_modal_open', { screen_context: 'payment_retry' });
  }

  paymentRetryTryAgainClicked() {
    this.trackEvent('funnel_modal_click', { screen_context: 'payment_retry', value: 'try_again' });
  }

  paymentRetryCloseClicked() {
    this.trackEvent('funnel_modal_click', { screen_context: 'payment_retry', value: 'close' });
  }

  setUserAge(age: number) {
    this.setUserProperties({
      user_age: age,
    });
  }
}
